import { z } from 'zod';
import { DISCIPLINES, type Discipline } from './naming.ts';

/**
 * `data/warranties.json`.
 *
 * The signature feature. Switching the layer surfaces the warranty in the same
 * motion — «السباكة — ضمان ١٥ سنة» arrives *with* the pipes, not in a footer
 * table. Keys are disciplines so `LayerController.onChange` can look them up
 * with no mapping layer in between.
 *
 * `envelope`, `transport` and `garage` have no geometry layer of their own.
 * They are project-wide guarantees, surfaced on the unit panel instead. They
 * live in this file anyway, so the operator's mental model stays simple: one
 * file holds every warranty the project makes.
 */

export const WarrantyItemSchema = z.strictObject({
  labelAr: z.string().min(1),
  years: z.number().int().positive(),
  noteAr: z.string().min(1).optional(),
});

const items = () => z.array(WarrantyItemSchema).min(1).optional();

/**
 * Written out rather than generated from a key list so the file is strict:
 * a typo like `"mecha"` fails the build instead of silently disappearing.
 */
export const WarrantiesSchema = z.strictObject({
  arch: items(),
  struct: items(),
  mech: items(),
  elec: items(),
  envelope: items(),
  transport: items(),
  garage: items(),
});

export const WARRANTY_KEYS = [
  ...DISCIPLINES,
  'envelope',
  'transport',
  'garage',
] as const;
export type WarrantyKey = (typeof WARRANTY_KEYS)[number];

export type WarrantyItem = z.infer<typeof WarrantyItemSchema>;
export type Warranties = z.infer<typeof WarrantiesSchema>;

export function isDisciplineKey(key: WarrantyKey): key is Discipline {
  return (DISCIPLINES as readonly string[]).includes(key);
}

/** Warranties the layer bar owns, keyed by discipline. Never throws. */
export function warrantiesForDiscipline(
  warranties: Warranties,
  discipline: Discipline,
): WarrantyItem[] {
  return warranties[discipline] ?? [];
}

/** Warranties with no layer of their own — rendered on the unit panel. */
export function projectWideWarranties(
  warranties: Warranties,
): Array<{ key: WarrantyKey; items: WarrantyItem[] }> {
  return WARRANTY_KEYS.filter((key) => !isDisciplineKey(key))
    .map((key) => ({ key, items: warranties[key] ?? [] }))
    .filter((entry) => entry.items.length > 0);
}

/** The longest guarantee on offer — the headline number worth leading with. */
export function longestWarrantyYears(warranties: Warranties): number {
  let max = 0;
  for (const key of WARRANTY_KEYS) {
    for (const item of warranties[key] ?? []) {
      if (item.years > max) max = item.years;
    }
  }
  return max;
}
